import { useApolloClient } from "@apollo/client";
import { uniqBy } from "lodash";

import { serializeInventoryProductFromSearchResult } from "flows/Inventory/shared/models/inventoryProduct/serializer";
import { InventoryProduct } from "flows/Inventory/shared/models/inventoryProduct/types";
import {
  SearchProductsByEanDocument,
  SearchProductsByEanQuery,
  SearchProductsByEanQueryVariables,
  SearchProductsByTextDocument,
  SearchProductsByTextQuery,
  SearchProductsByTextQueryVariables,
} from "flows/Inventory/shared/queries/product/product.generated";
import { reportErrorsToDDAndThrow } from "utils/datadog";
import { isNullOrUndefined } from "utils/tsHelpers";

import {
  UpdateProductBbdDocument,
  UpdateProductBbdMutation,
  UpdateProductBbdMutationVariables,
} from "../queries/bbd/bbd.generated";
import {
  GetProductWithBbdDocument,
  GetProductWithBbdQuery,
  GetProductWithBbdQueryVariables,
} from "../queries/product/product.generated";

export function useBbdCorrectionServices() {
  const client = useApolloClient();

  const fetchProducts = async (barcode: string): Promise<InventoryProduct[]> => {
    return reportErrorsToDDAndThrow("fetch products by ean", async () => {
      const { data: dataProductsByEan } = await client.query<
        SearchProductsByEanQuery,
        SearchProductsByEanQueryVariables
      >({
        query: SearchProductsByEanDocument,
        variables: { searchProductsByEanInput: { ean: barcode }, isAlwaysInbound: true },
        fetchPolicy: "no-cache",
      });
      return dataProductsByEan.searchUnitsByEan.units.map((unit) =>
        serializeInventoryProductFromSearchResult(unit.product),
      );
    });
  };

  const fetchProductsByText = async (query: string): Promise<InventoryProduct[]> => {
    const { data } = await client.query<
      SearchProductsByTextQuery,
      SearchProductsByTextQueryVariables
    >({
      query: SearchProductsByTextDocument,
      variables: { searchProductsByTextInput: { query } },
      fetchPolicy: "no-cache",
    });

    const filteredUnits = data.searchUnitsByText.units.filter((unit) => !!unit.product);
    const uniqueProducts = uniqBy(filteredUnits, (unit) => unit.product.sku);

    return uniqueProducts.map((unit) => serializeInventoryProductFromSearchResult(unit.product));
  };

  const fetchProductWithBBDTask = async (sku: string): Promise<void> => {
    return reportErrorsToDDAndThrow("fetchProductWithBBDTask", async () => {
      try {
        await client.query<GetProductWithBbdQuery, GetProductWithBbdQueryVariables>({
          query: GetProductWithBbdDocument,
          variables: { sku },
          fetchPolicy: "network-only",
        });
      } catch (e: any) {
        switch (e?.graphQLErrors?.[0]?.extensions?.code) {
          case "NOT_BBD_ELIGIBLE": {
            throw new Error("productIsNotBbd");
          }
          case "BBD_INPUT_PENDING": {
            throw new Error("hasOpenTask");
          }
          default:
            throw e;
        }
      }
    });
  };

  const updateProductBbd = async (
    sku: string,
    closestBbd: Date,
  ): Promise<{ closestBbd: Date; initialClosestBbd: Date | null }> => {
    return reportErrorsToDDAndThrow("updateProductBbd", async () => {
      if (isNullOrUndefined(sku)) {
        throw new Error("No selectedInventoryProductSku present in context");
      }
      const closestBBDAfterOutbound = closestBbd.toISOString();
      await client.mutate<UpdateProductBbdMutation, UpdateProductBbdMutationVariables>({
        mutation: UpdateProductBbdDocument,
        variables: {
          input: { closestBBDAfterOutbound, sku },
        },
      });
      return { closestBbd, initialClosestBbd: null };
    });
  };

  return {
    fetchProducts,
    fetchProductsByText,
    fetchProductWithBBDTask,
    updateProductBbd,
  };
}
